import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/format";
import PatternOverlay from "@/components/PatternOverlay";
import LayerYourSignature from "@/components/LayerYourSignature";

const PALETTE_NAMES = new Set(["Blæk", "Vétiver", "Ivoire"]);

export default function ProductDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState("");
  const { add } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setP(data);
      } catch (e) {
        setError("Fragrance not found.");
      }
    })();
  }, [id]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
        <div className="font-serif text-4xl">{error}</div>
        <Link to="/shop" className="mt-6 inline-block text-xs uppercase tracking-[0.22em] text-primary">
          Back to shop
        </Link>
      </div>
    );
  }

  if (!p) return <div className="py-32 text-center text-muted-foreground" data-testid="product-loading">Loading…</div>;

  const gallery = [p.image_url, ...(p.gallery || [])].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-primary" data-testid="product-back-link">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-8">
        {/* Gallery */}
        <div className="space-y-4">
          <div className="aspect-[4/5] overflow-hidden bg-secondary">
            <img src={gallery[0]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-3 gap-3">
              {gallery.slice(0, 3).map((g, i) => (
                <div key={i} className="aspect-square overflow-hidden bg-secondary">
                  <img src={g} alt="thumb" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-primary">
            <span className="normal-case">{p.brand}</span> · {p.concentration}
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 tracking-tight leading-[1.02]" data-testid="product-title">
            {p.name}
          </h1>
          <div className="mt-4 text-lg tracking-widest" data-testid="product-price">{formatGBP(p.price)}</div>

          <p className="mt-8 text-muted-foreground leading-relaxed">{p.description}</p>

          {/* Notes pyramid */}
          <div className="pattern-bg mt-10 space-y-5 border-t border-border pt-8 pb-6">
            <PatternOverlay opacity={0.13} size={400} />
            {[
              { label: "Top", data: p.notes_top },
              { label: "Heart", data: p.notes_heart },
              { label: "Base", data: p.notes_base },
            ].map((n) => (
              <div key={n.label} className="grid grid-cols-4 gap-4">
                <div className="text-xs uppercase tracking-[0.28em] text-primary col-span-1">{n.label}</div>
                <div className="col-span-3 text-sm text-muted-foreground">{(n.data || []).join(" · ")}</div>
              </div>
            ))}
          </div>

          {/* Add to cart */}
          <div className="mt-10 flex flex-wrap gap-5 items-center">
            <div className="flex items-center border border-border">
              <button
                onClick={() => setQty((v) => Math.max(1, v - 1))}
                className="p-3 text-muted-foreground hover:text-primary"
                data-testid="product-qty-minus"
              >
                <Minus size={14} />
              </button>
              <span className="w-10 text-center" data-testid="product-qty">{qty}</span>
              <button
                onClick={() => setQty((v) => v + 1)}
                className="p-3 text-muted-foreground hover:text-primary"
                data-testid="product-qty-plus"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              onClick={() => add(p, qty)}
              data-testid="add-to-cart-btn"
              className="bg-primary text-primary-foreground uppercase tracking-[0.22em] text-sm px-10 py-4 hover:opacity-90 transition-opacity"
            >
              Add to Cart
            </button>
          </div>

          <div className="mt-10 text-xs uppercase tracking-[0.22em] text-muted-foreground grid grid-cols-2 gap-4 border-t border-border pt-6">
            <div>Free shipping over £50</div>
            <div>30-day exchange window</div>
            <div>Cruelty-free · Vegan</div>
            <div>Handcrafted, small batch</div>
          </div>
        </div>
      </div>

      {PALETTE_NAMES.has(p.name) && (
        <div className="mt-20 -mx-6 md:-mx-12">
          <LayerYourSignature initialSelected={[p.name]} />
        </div>
      )}
    </div>
  );
}
